import { PhysioLayout } from "@/components/layout/physio-layout";
import { Link } from "wouter";
import { TrendingUp, ArrowRight, Stethoscope, Star, GraduationCap, Building2, Award, BookOpen } from "lucide-react";

const CAREER_PATHS = [
  {
    href: "/physio/career/band-progression",
    icon: TrendingUp,
    title: "Band Progression: 5 → 6 → 7",
    description: "Structured guidance on the competencies, experience, and interview preparation needed to progress from Band 5 through to Band 7 specialist physiotherapist.",
    colour: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    badge: "Foundation",
  },
  {
    href: "/physio/career/esp",
    icon: Stethoscope,
    title: "Extended Scope Practice (ESP)",
    description: "Extended Scope Practitioner roles in rheumatology: scope of practice, governance frameworks, injection therapy, imaging referral pathways, and MSK ESP career progression.",
    colour: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-100",
    badge: "Advanced",
  },
  {
    href: "/physio/career/app",
    icon: Star,
    title: "Advanced & Consultant Practitioner",
    description: "Band 8a–8d Advanced Physiotherapy Practitioner and Consultant Physiotherapist pathways. HEE APP framework, formal recognition, and strategic leadership development.",
    colour: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-100",
    badge: "Specialist",
  },
  {
    href: "/physio/career/academic",
    icon: GraduationCap,
    title: "Academic Physiotherapy",
    description: "PhD routes, NIHR clinical academic fellowships, honorary academic appointments, and building a clinical academic career alongside NHS practice.",
    colour: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-100",
    badge: "Academic",
  },
  {
    href: "/physio/career/acpr-specialist",
    icon: Award,
    title: "ACPR Specialist Career Route",
    description: "Developing as a rheumatology physiotherapy specialist via the ACPR pathway: membership, clinical standards, specialist CPD, and recognition within the profession.",
    colour: "text-rose-600",
    bg: "bg-rose-50",
    border: "border-rose-100",
    badge: "Specialist",
  },
  {
    href: "/physio/career/leadership",
    icon: Building2,
    title: "Leadership & Management",
    description: "NHS leadership frameworks, service management, Head of Service roles, NHS Leadership Academy programmes, and strategies for clinical leadership development.",
    colour: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-100",
    badge: "Leadership",
  },
];

const BADGE_COLOURS: Record<string, string> = {
  Foundation: "bg-emerald-100 text-emerald-700",
  Advanced: "bg-blue-100 text-blue-700",
  Specialist: "bg-violet-100 text-violet-700",
  Academic: "bg-amber-100 text-amber-700",
  Leadership: "bg-teal-100 text-teal-700",
};

export default function PhysioCareerHub() {
  return (
    <PhysioLayout title="Career Pathways Hub">
      <div className="space-y-8">
        <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="h-7 w-7 text-emerald-300" />
            <span className="text-emerald-300 font-medium uppercase tracking-wider text-sm">Career Development</span>
          </div>
          <h2 className="text-2xl font-bold mb-2">Physiotherapy Career Pathways</h2>
          <p className="text-emerald-200 text-sm leading-relaxed max-w-2xl">
            Structured guidance for rheumatology physiotherapists at every stage. From Band 5 to Consultant Physiotherapist and clinical academic — explore the routes, requirements, and CPD needed to advance your career.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {CAREER_PATHS.map((path) => (
            <Link key={path.href} href={path.href}>
              <div className={`bg-white rounded-2xl border ${path.border} p-6 hover:shadow-md transition-all cursor-pointer group h-full flex flex-col`}>
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-10 h-10 rounded-xl ${path.bg} flex items-center justify-center`}>
                    <path.icon className={`h-5 w-5 ${path.colour}`} />
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BADGE_COLOURS[path.badge]}`}>{path.badge}</span>
                </div>
                <h3 className={`font-bold text-slate-900 mb-2 group-hover:${path.colour} transition-colors`}>{path.title}</h3>
                <p className="text-sm text-slate-500 flex-1 leading-relaxed">{path.description}</p>
                <div className={`flex items-center gap-1 text-xs font-medium mt-4 ${path.colour}`}>
                  Explore pathway <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900 mb-2">Career Planning Checklist</h3>
          <p className="text-sm text-slate-600 mb-3">Use these steps to actively plan your physiotherapy career progression:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              "Review current band and competencies",
              "Set a 6-month and 2-year career goal",
              "Identify CPD gaps and plan learning",
              "Discuss with your supervisor / line manager",
              "Build your APP portfolio evidence",
              "Explore ACPR membership and specialist CPD",
              "Connect with clinical networks (CSP, ACPR)",
              "Consider postgraduate study options",
            ].map((step) => (
              <div key={step} className="flex items-center gap-2 text-sm text-slate-700">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                {step}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
